import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useTranslate } from "@refinedev/core";
import {
  Grid2 as Grid,
  Typography,
  TextField,
  FormControlLabel,
  Button,
  Stack,
} from "@mui/material";

import { Switch } from "@shared/components/DataEntry";
import { useTiledeskStatusQuery } from "@modules/tiledesk/services/queries/getTiledeskStatus";
import { useEnableTiledeskMutation } from "@modules/tiledesk/services/mutations/enableTiledesk";
import { useDisableTiledeskMutation } from "@modules/tiledesk/services/mutations/disableTiledesk";
import { useDeleteTiledeskMutation } from "@modules/tiledesk/services/mutations/deleteTiledesk";
import { useCreateTiledeskAgentsMutation } from "@modules/tiledesk/services/mutations/createTiledeskAgents";
import { useOpenTiledeskDashboardMutation } from "@modules/tiledesk/services/mutations/openTiledeskDashboard";

export function TiledeskFields() {
  const translate = useTranslate();
  const { id } = useParams();
  const agreementId = id as string;

  const [agentEmailsInput, setAgentEmailsInput] = useState("");
  const [hasEditedEmails, setHasEditedEmails] = useState(false);

  const { data: status, isLoading } = useTiledeskStatusQuery(agreementId);
  const { mutateAsync: enable, isPending: isEnabling } =
    useEnableTiledeskMutation(agreementId);
  const { mutateAsync: disable, isPending: isDisabling } =
    useDisableTiledeskMutation(agreementId);
  const { mutateAsync: createAgents, isPending: isCreatingAgents } =
    useCreateTiledeskAgentsMutation(agreementId);
  const { mutateAsync: openDashboard, isPending: isOpeningDashboard } =
    useOpenTiledeskDashboardMutation();
  const { mutateAsync: deleteChat, isPending: isDeleting } =
    useDeleteTiledeskMutation(agreementId);

  const isPending = isEnabling || isDisabling || isCreatingAgents || isDeleting;

  // Pré-preenche com os e-mails já salvos no convênio; se o admin editar,
  // não sobrescreve mais (evita perder o que ele está digitando).
  useEffect(() => {
    if (!hasEditedEmails && status?.agentEmails?.length) {
      setAgentEmailsInput(status.agentEmails.join(", "));
    }
  }, [status?.agentEmails, hasEditedEmails]);

  const parsedEmails = agentEmailsInput
    .split(",")
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);

  const persistedEmails = status?.agentEmails ?? [];
  const hasAgentCreated = persistedEmails.length > 0;
  const allTypedEmailsAlreadyCreated =
    parsedEmails.length > 0 &&
    parsedEmails.every((email) => persistedEmails.includes(email));

  const toggle = async () => {
    if (status?.enabled) {
      await disable();
      return;
    }

    await enable({ agreementId, agentEmails: parsedEmails });
  };

  const handleCreateAgents = async () => {
    await createAgents({ agreementId, emails: parsedEmails });
  };

  const handleDelete = async () => {
    if (!window.confirm(translate("tiledesk.actions.delete_confirm"))) return;
    await deleteChat();
  };

  // Abre a aba já no clique (antes do await) para não ser barrada pelo bloqueador de pop-up.
  const handleOpenDashboard = async () => {
    const tab = window.open("about:blank", "_blank");
    try {
      const { url } = await openDashboard({ agreementId, role: "admin" });
      if (tab) {
        tab.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch {
      tab?.close();
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Typography style={{ fontSize: 18, color: "primary.main" }}>
          {translate("tiledesk.sections.title")}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Stack spacing={1}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              label={translate("tiledesk.fields.agent_emails")}
              value={agentEmailsInput}
              onChange={(event) => {
                setHasEditedEmails(true);
                setAgentEmailsInput(event.target.value);
              }}
              sx={{ flex: "1 1 360px", minWidth: 0 }}
            />
            <Button
              variant="outlined"
              onClick={handleCreateAgents}
              disabled={
                isPending || parsedEmails.length === 0 || allTypedEmailsAlreadyCreated
              }
              sx={{ flexShrink: 0, whiteSpace: "nowrap", alignSelf: { xs: "flex-start", sm: "center" } }}
            >
              {allTypedEmailsAlreadyCreated
                ? translate("tiledesk.actions.agent_already_created")
                : translate("tiledesk.actions.create_agent")}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {translate("tiledesk.fields.agent_emails_instructions")}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Stack spacing={0.5}>
          <FormControlLabel
            control={
              <Switch
                checked={!!status?.enabled}
                onChange={toggle}
                disabled={isLoading || isPending || (!status?.enabled && !hasAgentCreated)}
              />
            }
            label={translate("tiledesk.fields.enabled")}
          />
          {!hasAgentCreated && (
            <Typography variant="caption" color="text.secondary">
              {translate("tiledesk.fields.enable_requires_agent")}
            </Typography>
          )}
        </Stack>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Typography variant="body2" color="text.secondary">
            {status?.provisioned
              ? translate("tiledesk.fields.provisioned", { projectId: status.projectId })
              : translate("tiledesk.fields.not_provisioned")}
          </Typography>
          {status?.provisioned && (
            <Button
              variant="outlined"
              onClick={handleOpenDashboard}
              disabled={isOpeningDashboard}
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              {translate("tiledesk.actions.open_dashboard")}
            </Button>
          )}
          {status?.provisioned && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              disabled={isPending}
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              {translate("tiledesk.actions.delete")}
            </Button>
          )}
        </Stack>
        {status?.accountEmail && (
          <Typography variant="caption" color="text.secondary">
            {translate("tiledesk.fields.account_email", { email: status.accountEmail })}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}
